import { CREDIT_PRICE, CreateEvent, OrgFullDetail, TicketTierType } from "@garther/interfaces";
import { Box, Button, CustomEditor, ImageInput, Input, Select } from "@garther/ui/components";
import { toast, useI18n } from "@garther/ui/libs";
import { useForm } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import { RawLayout } from "@/components/layout";
import { eventsApiClient, publicApiClient } from "@/libs/api";

interface CreateEventPageProps {
  org: OrgFullDetail;
}

interface TicketTierFormData {
  type: TicketTierType;
  title: string;
  description: string;
  cap: number;
  price: number;
}

interface FormData {
  title: string;
  description: string;
  banner: string;
  featuredImages: string[];
  date: string;
  locationId: string;
  categoryId: string;
  cap: number;
  ticketTiers: TicketTierFormData[];
}

export function CreateEventPage({ org }: CreateEventPageProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormData>({
    defaultValues: {
      title: "",
      description: "",
      banner: "",
      featuredImages: [""],
      date: "",
      locationId: "",
      categoryId: "",
      cap: 0,
      ticketTiers: [
        {
          type: TicketTierType.NORMAL,
          title: "",
          description: "",
          cap: 0,
          price: 0
        }
      ]
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true);
      try {
        // Calculate total cap from ticket tiers
        const totalTicketCap = value.ticketTiers.reduce((sum, tier) => sum + tier.cap, 0);
        const eventCap = value.cap > 0 ? Math.min(value.cap, totalTicketCap) : totalTicketCap;

        const eventData: CreateEvent = {
          hostId: org.id,
          title: value.title.trim(),
          description: value.description.trim(),
          banner: value.banner.trim(),
          featuredImages: value.featuredImages.filter((img) => img.trim() !== ""),
          date: new Date(value.date),
          locationId: value.locationId,
          categoryId: null,
          cap: eventCap,
          ticketTiers: value.ticketTiers.map((tier) => ({
            type: tier.type,
            title: tier.title.trim(),
            description: tier.description.trim(),
            cap: tier.cap,
            price: tier.price / CREDIT_PRICE
          }))
        };

        const createdEvent = await eventsApiClient.createEvent(eventData);
        toast.success(t("org.events.createSuccess") || "Sự kiện đã được tạo thành công!");
        navigate({
          to: "/org/$orgId/event/$eventId",
          params: { orgId: org.id, eventId: createdEvent.id }
        });
      } catch (error) {
        console.error("Error creating event:", error);
        toast.error(t("org.events.createError") || "Có lỗi xảy ra khi tạo sự kiện");
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  // const addFeaturedImage = () => {
  //   form.setFieldValue("featuredImages", [...form.state.values.featuredImages, ""]);
  // };

  // const removeFeaturedImage = (index: number) => {
  //   form.setFieldValue(
  //     "featuredImages",
  //     form.state.values.featuredImages.filter((_, i) => i !== index)
  //   );
  // };

  const locationOptions = org.locations.map((loc) => ({
    value: loc.id,
    label: loc.address
  }));

  const ticketTierTypeOptions = [
    { value: TicketTierType.NORMAL, label: "Thường" },
    { value: TicketTierType.GROUP, label: "Nhóm" },
    { value: TicketTierType.GROUP_WOMEN, label: "Nhóm Nữ" },
    { value: TicketTierType.GROUP_MEN, label: "Nhóm Nam" },
    { value: TicketTierType.GROUP_WITH_FRIEND, label: "Nhóm Bạn" }
  ];

  return (
    <RawLayout>
      <div className="py-4 md:py-6 lg:py-8 px-0 sm:px-2 md:px-4 lg:px-8 space-y-6">
        <div className="space-y-4 sm:space-y-6 max-w-7xl mx-auto">
          <div className="px-4 md:px-6">
            <p className="text-responsive-xl font-normal text-text-primary mb-2">Tạo sự kiện mới</p>
            <p className="text-responsive-base text-text-secondary">
              Điền thông tin để tạo sự kiện mới cho tổ chức {org.name}
            </p>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <div className="px-4 md:px-6 py-2">
              <p className="text-text-secondary text-responsive-lg font-normal">{t("org.edit.basicInfo")}</p>
            </div>
            <Box className="space-y-6 mb-6">
              {/* Title */}
              <form.Field
                name="title"
                validators={{
                  onChange: ({ value }) => (!value?.trim() ? "Tiêu đề là bắt buộc" : undefined)
                }}
              >
                {(field) => (
                  <Input
                    label="Tiêu đề sự kiện"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="Nhập tiêu đề sự kiện"
                    required
                    error={field.state.meta.errors[0] || undefined}
                    disabled={isSubmitting}
                  />
                )}
              </form.Field>

              {/* Description */}
              <form.Field
                name="description"
                validators={{
                  onChange: ({ value }) => (!value?.trim() ? "Mô tả là bắt buộc" : undefined)
                }}
              >
                {(field) => (
                  <CustomEditor
                    label="Mô tả sự kiện"
                    placeholder="Nhập mô tả chi tiết về sự kiện"
                    required
                    error={field.state.meta.errors[0] || undefined}
                    value={field.state.value}
                    onChangeCallback={field.handleChange}
                    disabled={isSubmitting}
                    config={{
                      toolbar: {
                        items: [
                          "bold",
                          "italic",
                          "underline",
                          "strikethrough",
                          "removeFormat",
                          "link",
                          "bulletedList",
                          "numberedList",
                          "|",
                          "blockQuote",
                          "|",
                          "undo",
                          "redo"
                        ],
                        shouldNotGroupWhenFull: false
                      }
                    }}
                  />
                )}
              </form.Field>

              {/* Banner */}
              <form.Field
                name="banner"
                validators={{
                  onChange: ({ value }) => {
                    if (value && value.trim() && !/^https?:\/\/.+/.test(value.trim())) {
                      return t("org.edit.errors.invalidUrl");
                    }
                    return undefined;
                  }
                }}
              >
                {(field) => (
                  <ImageInput
                    value={field.state.value}
                    onChange={field.handleChange}
                    uploadFn={publicApiClient.uploadImage}
                    label={"Banner"}
                    required={true}
                    previewSize="w-full max-w-md h-48"
                    disabled={isSubmitting}
                    toastSuccess={toast.success}
                    toastError={toast.error}
                    successMessage={t("org.edit.avatarUploadSuccess")}
                    errorMessage={t("org.edit.avatarUploadError")}
                    invalidImageTypeMessage={t("org.edit.errors.invalidImageType")}
                    fileTooLargeMessage={t("profile.basicInfo.fileTooLarge")}
                    alt="Banner preview"
                  />
                )}
              </form.Field>

              {/* Featured Images */}
              {/* <div className="space-y-2">
                <label className="text-text-secondary font-medium text-responsive-sm">Hình ảnh nổi bật</label>
                {form.state.values.featuredImages.map((_, index) => (
                  <div key={index} className="flex gap-2">
                    <form.Field name={`featuredImages[${index}]`}>
                      {(field) => (
                        <Input
                          type="url"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          placeholder="https://example.com/image.jpg"
                          disabled={isSubmitting}
                          className="flex-1"
                        />
                      )}
                    </form.Field>
                    {form.state.values.featuredImages.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => removeFeaturedImage(index)}
                        disabled={isSubmitting}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={addFeaturedImage} disabled={isSubmitting}>
                  <Plus className="h-4 w-4 mr-2" />
                  Thêm hình ảnh
                </Button>
              </div>  */}

              {/* Date */}
              <form.Field
                name="date"
                validators={{
                  onChange: ({ value }) => {
                    if (!value) return "Ngày giờ là bắt buộc";
                    if (new Date(value) < new Date()) {
                      return "Ngày giờ phải trong tương lai";
                    }
                    return undefined;
                  }
                }}
              >
                {(field) => (
                  <Input
                    label="Ngày và giờ"
                    type="datetime-local"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    required
                    error={field.state.meta.errors[0] || undefined}
                    disabled={isSubmitting}
                  />
                )}
              </form.Field>

              {/* Location */}
              <form.Field
                name="locationId"
                validators={{
                  onChange: ({ value }) => (!value ? "Địa điểm là bắt buộc" : undefined)
                }}
              >
                {(field) => (
                  <Select
                    label="Địa điểm"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    options={locationOptions}
                    placeholder="Chọn địa điểm"
                    required
                    error={field.state.meta.errors[0] || undefined}
                    disabled={isSubmitting}
                  />
                )}
              </form.Field>

              {/* Category */}
              {/* <form.Field name="categoryId">
                {(field) => (
                  <Input
                    label="ID Danh mục (tùy chọn)"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="Nhập ID danh mục"
                    disabled={isSubmitting}
                  />
                )}
              </form.Field> */}

              {/* Capacity */}
              <form.Field
                name="cap"
                validators={{
                  onChange: ({ value }) => {
                    if (!value || value <= 0) return "Sức chứa phải lớn hơn 0";
                    return undefined;
                  }
                }}
              >
                {(field) => (
                  <Input
                    label="Sức chứa tối đa"
                    type="number"
                    value={field.state.value.toString()}
                    onChange={(e) => field.handleChange(parseInt(e.target.value) || 0)}
                    placeholder="Nhập sức chứa tối đa"
                    required
                    error={field.state.meta.errors[0] || undefined}
                    disabled={isSubmitting}
                  />
                )}
              </form.Field>
            </Box>

            {/* Ticket Tiers */}
            <form.Field name="ticketTiers">
              {(ticketTiersField) => (
                <>
                  <div className="flex items-end justify-between px-4 md:px-6 py-2">
                    <p className="text-text-secondary text-responsive-lg font-normal">CÁC LOẠI VÉ</p>
                  </div>
                  <Box className="space-y-6 mb-6">
                    <div className="space-y-4">
                      {ticketTiersField.state.value.map((_, tierIndex) => (
                        <Box key={tierIndex} className="space-y-4 p-4 border border-border">
                          <div className="flex items-center justify-between">
                            <h3 className="text-responsive-base font-medium text-text-primary">
                              Loại vé #{tierIndex + 1}
                            </h3>
                            {ticketTiersField.state.value.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                onClick={() => ticketTiersField.removeValue(tierIndex)}
                                disabled={isSubmitting}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>

                          <form.Field
                            name={`ticketTiers[${tierIndex}].type`}
                            validators={{
                              onChange: ({ value }) => (!value ? "Loại vé là bắt buộc" : undefined)
                            }}
                          >
                            {(field) => (
                              <Select
                                label="Loại vé"
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e.target.value as TicketTierType)}
                                options={ticketTierTypeOptions}
                                required
                                error={field.state.meta.errors[0] || undefined}
                                disabled={isSubmitting}
                              />
                            )}
                          </form.Field>

                          <form.Field
                            name={`ticketTiers[${tierIndex}].title`}
                            validators={{
                              onChange: ({ value }) =>
                                !value?.trim() ? "Tên loại vé là bắt buộc" : undefined
                            }}
                          >
                            {(field) => (
                              <Input
                                label="Tên loại vé"
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e.target.value)}
                                placeholder="Ví dụ: Vé thường, Vé VIP"
                                required
                                error={field.state.meta.errors[0] || undefined}
                                disabled={isSubmitting}
                              />
                            )}
                          </form.Field>

                          <form.Field name={`ticketTiers[${tierIndex}].description`}>
                            {(field) => (
                              <CustomEditor
                                label="Mô tả"
                                placeholder="Mô tả về loại vé này"
                                error={field.state.meta.errors[0] || undefined}
                                value={field.state.value || ""}
                                onChangeCallback={field.handleChange}
                                disabled={isSubmitting}
                                config={{
                                  toolbar: {
                                    items: [
                                      "bold",
                                      "italic",
                                      "underline",
                                      "strikethrough",
                                      "removeFormat",
                                      "link",
                                      "bulletedList",
                                      "numberedList",
                                      "|",
                                      "blockQuote",
                                      "|",
                                      "undo",
                                      "redo"
                                    ],
                                    shouldNotGroupWhenFull: false
                                  }
                                }}
                              />
                            )}
                          </form.Field>

                          <div className="grid grid-cols-2 gap-4">
                            <form.Field
                              name={`ticketTiers[${tierIndex}].cap`}
                              validators={{
                                onChange: ({ value }) => {
                                  if (!value || value <= 0) return "Số lượng phải lớn hơn 0";
                                  return undefined;
                                }
                              }}
                            >
                              {(field) => (
                                <Input
                                  label="Số lượng"
                                  type="number"
                                  value={field.state.value.toString()}
                                  onChange={(e) => field.handleChange(parseInt(e.target.value) || 0)}
                                  placeholder="0"
                                  required
                                  error={field.state.meta.errors[0] || undefined}
                                  disabled={isSubmitting}
                                />
                              )}
                            </form.Field>

                            <form.Field
                              name={`ticketTiers[${tierIndex}].price`}
                              validators={{
                                onChange: ({ value }) => {
                                  if (value < 0) return "Giá không được âm";
                                  return undefined;
                                }
                              }}
                            >
                              {(field) => (
                                <Input
                                  label="Giá (VND)"
                                  type="number"
                                  value={field.state.value.toString()}
                                  onChange={(e) => field.handleChange(parseFloat(e.target.value) || 0)}
                                  placeholder="0"
                                  required
                                  error={field.state.meta.errors[0] || undefined}
                                  disabled={isSubmitting}
                                />
                              )}
                            </form.Field>
                          </div>
                        </Box>
                      ))}
                    </div>
                    <div className="flex gap-4 justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        padding="px-8 py-2"
                        onClick={() =>
                          ticketTiersField.pushValue({
                            type: TicketTierType.NORMAL,
                            title: "",
                            description: "",
                            cap: 0,
                            price: 0
                          })
                        }
                        disabled={isSubmitting}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Thêm loại vé
                      </Button>
                    </div>
                  </Box>
                </>
              )}
            </form.Field>
            <div className="flex gap-4 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate({ to: "/org/$orgId", params: { orgId: org.id } })}
                disabled={isSubmitting}
              >
                Hủy
              </Button>
              <Button type="submit" variant="default" disabled={isSubmitting}>
                {isSubmitting ? "Đang tạo..." : "Tạo sự kiện"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </RawLayout>
  );
}
